import {
  aiDecisionSchema,
  aiRequestSchema,
  aiUsageBudgetSchema,
  availableLoadSchema,
  cardioLogSchema,
  dailyActivitySchema,
  evidenceSearchSchema,
  evidenceSourceSchema,
  dailyWeightSchema,
  mealLogSchema,
  mealPlanSchema,
  nutritionTargetSchema,
  plannedMealSchema,
  pantryItemSchema,
  shoppingListItemSchema,
  shoppingListSchema,
  userRecipeSettingSchema,
  cardioPrescriptionSchema,
  equipmentSchema,
  gymSchema,
  loadRecommendationSchema,
  performedSetSchema,
  profileSchema,
  progressionStateSchema,
  readinessCheckinSchema,
  restEventSchema,
  sessionExerciseSchema,
  setPrescriptionSchema,
  trainingBlockSchema,
  trainingDecisionSchema,
  trainingSessionSchema,
  trainingWeekSchema,
  userPreferencesSchema,
  bodyMeasurementSchema,
  weeklyCheckinSchema,
  warmupPrescriptionSchema,
  workoutLogSchema,
} from '@cadencia/contracts';
import type { CadenciaDb } from './db.js';
import { logger } from '../lib/logger.js';

/**
 * Normalizacion al LEER de la base local (deuda D-4, riesgo R-16).
 *
 * El problema real que resuelve: una columna añadida despues con `DEFAULT` no
 * incrementa `sync_seq` ni `updated_at` en las filas que ya existian, asi que el
 * servidor nunca vuelve a mandarlas y la copia local se queda con la forma
 * antigua para siempre. El codigo que espera el campo nuevo recibe `undefined` y
 * revienta lejos de la causa: en la Fase 3 eso dejo una pantalla en blanco sin
 * un solo mensaje de error.
 *
 * Se hace con un `hook('reading')` de Dexie, no repositorio por repositorio,
 * porque asi cubre TODA consulta —incluidas las que se escriban manana— desde un
 * unico sitio.
 *
 * Politica ante una fila que no valida: se registra y **se devuelve tal cual**.
 * Descartarla en silencio perderia datos del usuario; hacerla estallar dejaria
 * la app inutilizable por una fila rara. Lo que no puede pasar es que no se
 * entere nadie.
 */
export type EsquemaLectura = {
  safeParse: (valor: unknown) => {
    success: boolean;
    data?: unknown;
    // `issues` lo usa la importacion del §8.2 para separar un fallo de FORMA
    // —aborta— de uno de RANGO —se descarta la fila y sigue—.
    error?: { issues?: readonly { code?: unknown; path?: unknown[]; message?: unknown }[] };
  };
};

const ESQUEMAS_POR_TABLA: Record<string, EsquemaLectura> = {
  profiles: profileSchema,
  userPreferences: userPreferencesSchema,
  gyms: gymSchema,
  equipment: equipmentSchema,
  availableLoads: availableLoadSchema,
  trainingBlocks: trainingBlockSchema,
  trainingWeeks: trainingWeekSchema,
  trainingSessions: trainingSessionSchema,
  sessionExercises: sessionExerciseSchema,
  setPrescriptions: setPrescriptionSchema,
  warmupPrescriptions: warmupPrescriptionSchema,
  readinessCheckins: readinessCheckinSchema,
  workoutLogs: workoutLogSchema,
  performedSets: performedSetSchema,
  restEvents: restEventSchema,
  loadRecommendations: loadRecommendationSchema,
  progressionState: progressionStateSchema,
  trainingDecisions: trainingDecisionSchema,
  cardioPrescriptions: cardioPrescriptionSchema,
  cardioLogs: cardioLogSchema,
  dailyWeights: dailyWeightSchema,
  nutritionTargets: nutritionTargetSchema,
  mealPlans: mealPlanSchema,
  plannedMeals: plannedMealSchema,
  mealLogs: mealLogSchema,
  userRecipeSettings: userRecipeSettingSchema,
  pantryItems: pantryItemSchema,
  shoppingLists: shoppingListSchema,
  shoppingListItems: shoppingListItemSchema,
  bodyMeasurements: bodyMeasurementSchema,
  weeklyCheckins: weeklyCheckinSchema,
  dailyActivity: dailyActivitySchema,
  evidenceSearches: evidenceSearchSchema,
  evidenceSources: evidenceSourceSchema,
  aiRequests: aiRequestSchema,
  aiDecisions: aiDecisionSchema,
  aiUsageBudget: aiUsageBudgetSchema,
};

/** Cuantas filas invalidas se han registrado ya, por tabla: no se repite el aviso. */
const avisadas = new Set<string>();

export function normalizarFila(tabla: string, fila: unknown): unknown {
  const esquema = ESQUEMAS_POR_TABLA[tabla];
  if (esquema === undefined || fila === null || typeof fila !== 'object') return fila;

  const resultado = esquema.safeParse(fila);
  if (resultado.success) return resultado.data;

  if (!avisadas.has(tabla)) {
    avisadas.add(tabla);
    logger.warn('db', 'ROW_SHAPE_MISMATCH', 'Fila local que no encaja con su contrato', {
      tabla,
      id: (fila as { id?: unknown }).id,
    });
  }
  return fila;
}

/**
 * El esquema de una tabla, si esta aplicacion lo conoce.
 *
 * Lo usa la importacion del §8.2 para el paso 2. Se saca de aqui y no de una
 * segunda lista porque este mapa ya existe y ya cubre las 34 entidades: una
 * copia se quedaria coja en cuanto alguien añadiera la 35 y el sintoma seria
 * una entidad que se importa sin validar.
 */
export function esquemaDe(tabla: string): EsquemaLectura | undefined {
  return ESQUEMAS_POR_TABLA[tabla];
}

/** Instala la normalizacion en todas las tablas sincronizables. */
export function instalarNormalizacionAlLeer(database: CadenciaDb): void {
  for (const tabla of Object.keys(ESQUEMAS_POR_TABLA)) {
    database.table(tabla).hook('reading', (fila: unknown) => normalizarFila(tabla, fila));
  }
}

/** Solo para tests: permite volver a comprobar el aviso de fila invalida. */
export function reiniciarAvisosDeNormalizacion(): void {
  avisadas.clear();
}
